import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Ban,
  CalendarClock,
  Clock,
  ClipboardCheck,
  LogIn,
  LogOut,
  QrCode,
  RefreshCw,
  Users,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { useVisitorsBase } from "../../components/visitors/VisitorBase";
import { NoFacilitiesNotice, PageHeader, ScopeFilters, StatCard, StatusBadge } from "../../components/visitors/VisitorShared";
import { CheckInDialog } from "../../components/visitors/VisitorForms";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import { fmtDuration, fmtTime, tzOffset, visitorApi } from "../../components/visitors/visitorUtils";

const REFRESH_MS = 30000;

export default function VisitorsDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const base = useVisitorsBase();
  const scope = useVisitorScope();
  const [data, setData] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkInPreset, setCheckInPreset] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await visitorApi.get("/dashboard", { ...scope.params, tz_offset_minutes: tzOffset() }));
    } catch (e) {
      console.error("Error cargando dashboard de visitas:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, scope.facilityId]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const overstays = new Set(data?.overstays || []);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.dashboard.title")}
        subtitle={t("visitors.dashboard.subtitle")}
        actions={
          <>
            <Button variant="outline" size="icon" onClick={load} aria-label={t("visitors.common.refresh")}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate(`${base}/scan`)}>
              <QrCode className="h-4 w-4 mr-2" />
              {t("visitors.dashboard.quickScan")}
            </Button>
            <Button
              onClick={() => {
                setCheckInPreset(null);
                setCheckInOpen(true);
              }}
              disabled={scope.facilities.length === 0}
            >
              <LogIn className="h-4 w-4 mr-2" />
              {t("visitors.dashboard.quickCheckIn")}
            </Button>
          </>
        }
      />

      <ScopeFilters scope={scope} />
      {scope.loaded && scope.facilities.length === 0 && <NoFacilitiesNotice isManager={scope.isManager} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} tone="green" label={t("visitors.dashboard.insideNow")} value={data?.inside_count} />
        <StatCard icon={LogIn} tone="blue" label={t("visitors.dashboard.entriesToday")} value={data?.entries_today} />
        <StatCard icon={LogOut} tone="slate" label={t("visitors.dashboard.exitsToday")} value={data?.exits_today} />
        <StatCard icon={Ban} tone="red" label={t("visitors.dashboard.deniedToday")} value={data?.denied_today} />
        <StatCard
          icon={ClipboardCheck}
          tone="amber"
          label={t("visitors.dashboard.pendingApprovals")}
          value={data?.pending_approvals}
          onClick={() => navigate(`${base}/preauths`)}
        />
        <StatCard
          icon={CalendarClock}
          tone="violet"
          label={t("visitors.dashboard.expectedToday")}
          value={data?.expected_count}
          onClick={() => navigate(`${base}/preauths`)}
        />
        <StatCard
          icon={AlertTriangle}
          tone="red"
          label={t("visitors.dashboard.openIncidents")}
          value={data?.open_incidents}
          onClick={() => navigate(`${base}/incidents`)}
        />
        <StatCard icon={Clock} tone="slate" label={t("visitors.dashboard.avgDuration")} value={fmtDuration(data?.avg_duration_minutes)} />
      </div>

      {data?.my_shift && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <span className="font-semibold text-blue-900">
              {t("visitors.dashboard.myShift")} · {data.my_shift.workday_date}
            </span>
            <span>
              {t("visitors.dashboard.myShiftEntries")}: <strong>{data.my_shift.entries}</strong>
            </span>
            <span>
              {t("visitors.dashboard.myShiftExits")}: <strong>{data.my_shift.exits}</strong>
            </span>
            <span>
              {t("visitors.dashboard.myShiftIncidents")}: <strong>{data.my_shift.incidents}</strong>
            </span>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t("visitors.dashboard.peopleInside")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!data?.inside?.length ? (
              <p className="text-center py-8 text-slate-500">{t("visitors.dashboard.nobodyInside")}</p>
            ) : (
              <div className="divide-y">
                {data.inside.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setDetailId(v.id)}
                    className="w-full text-left py-3 flex items-center justify-between gap-3 hover:bg-slate-50 px-2 rounded"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{v.visitor_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {[v.facility_name, v.host_name, v.destination, v.vehicle?.plate].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium">{fmtTime(v.check_in_at)}</p>
                      <p className={`text-xs ${overstays.has(v.id) ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                        {fmtDuration(v.minutes_inside)}
                        {overstays.has(v.id) && ` · ${t("visitors.dashboard.overstay")}`}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5" />
                {t("visitors.dashboard.expectedVisits")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!data?.expected?.length ? (
                <p className="text-center py-4 text-slate-500 text-sm">{t("visitors.dashboard.noExpected")}</p>
              ) : (
                <div className="divide-y">
                  {data.expected.map((p) => (
                    <div key={p.id} className="py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{p.visitor_name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {fmtTime(p.valid_from)} · {p.host_name || p.facility_name}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCheckInPreset({ preauth: p });
                          setCheckInOpen(true);
                        }}
                      >
                        <LogIn className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t("visitors.dashboard.recentActivity")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!data?.recent?.length ? (
                <p className="text-center py-4 text-slate-500 text-sm">{t("visitors.common.noResults")}</p>
              ) : (
                <div className="divide-y">
                  {data.recent.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setDetailId(v.id)}
                      className="w-full text-left py-2 flex items-center justify-between gap-2 hover:bg-slate-50 rounded px-1"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{v.visitor_name}</p>
                        <p className="text-xs text-slate-500">{fmtTime(v.created_at)} · {v.check_in_by_name}</p>
                      </div>
                      <StatusBadge status={v.status} />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {scope.isManager && data?.pending_approvals > 0 && (
        <Badge variant="outline" className="border-amber-300 text-amber-800">
          {t("visitors.preauths.pendingNotice", { count: data.pending_approvals })}
        </Badge>
      )}

      <CheckInDialog open={checkInOpen} onOpenChange={setCheckInOpen} scope={scope} preset={checkInPreset} onDone={load} />
      <VisitDetailDialog
        visitId={detailId}
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
        scope={scope}
        onChanged={load}
      />
    </div>
  );
}
